import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import AccountLayout from '@/components/feature/AccountLayout';
import Toast from '@/components/common/Toast';
import ConfirmModal from '@/components/common/ConfirmModal';

import { useAuth } from '@/hooks/useAuth';
import {
  fetchGetReceivedLeads,
  fetchGetSentLeads,
  fetchAcceptLead,
  fetchDeclineLead,
  fetchCloseLead,
} from '@/service/api';

type LeadViewType = 'received' | 'sent';

const getStatusBadge = (status?: string) => {
  const normalized = (status || '').toUpperCase();
  switch (normalized) {
    case 'SENT':
    case 'PENDING':
    case 'NEW':
      return { label: '待处理', color: 'bg-amber-50 text-amber-700 border-amber-200/80' };
    case 'ACCEPTED':
      return { label: '已接受', color: 'bg-emerald-50 text-emerald-700 border-emerald-200/80' };
    case 'DECLINED':
      return { label: '已拒绝', color: 'bg-rose-50 text-rose-700 border-rose-200/80' };
    case 'CLOSED':
      return { label: '已关闭', color: 'bg-slate-100 text-slate-600 border-slate-200/80' };
    default:
      return { label: status || '未知状态', color: 'bg-slate-100 text-slate-600 border-slate-200/80' };
  }
};

export default function MyLeadsPage() {
  const [viewType, setViewType] = useState<LeadViewType>('received');
  const [leads, setLeads] = useState<Api.Lead.LeadResponse[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    type: 'accept' | 'decline' | 'close';
    id: string;
  } | null>(null);
  const [toast, setToast] = useState<{ message: string; type?: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const { isLoggedIn, user, rawUser, isEnterprise } = useAuth();
  const lastFetchKeyRef = useRef<string | null>(null);

  // 企业认证状态校验
  const certStatus = (rawUser?.certification_status || user?.verificationStatus || '').toUpperCase();
  const isEnterpriseCertified =
    isLoggedIn &&
    isEnterprise &&
    (certStatus === 'APPROVED' || certStatus === 'PASSED' || certStatus === 'SUCCESS' || user?.verified);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res =
        viewType === 'received'
          ? await fetchGetReceivedLeads({ page, page_size: 20 })
          : await fetchGetSentLeads({ page, page_size: 20 });
      setLeads(res.items || []);
      setTotal(res.total || 0);
    } catch (err: any) {
      console.error('获取线索列表失败:', err);
      setError(err instanceof Error ? err.message : '获取线索列表失败');
    } finally {
      setLoading(false);
    }
  }, [viewType, page]);

  useEffect(() => {
    if (!isEnterprise && viewType !== 'received') {
      setViewType('received');
    }
  }, [isEnterprise, viewType]);

  useEffect(() => {
    const fetchKey = `${viewType}-${page}`;
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;
    loadLeads();
  }, [viewType, page, loadLeads]);

  const handleTabChange = (type: LeadViewType) => {
    setViewType(type);
    setPage(1);
  };

  const handleOpenConfirmModal = (type: 'accept' | 'decline' | 'close', id?: string) => {
    if (!id || actionLoadingId) return;
    setConfirmModal({ type, id });
  };

  const handleExecuteAction = async () => {
    if (!confirmModal || actionLoadingId) return;
    const { type, id } = confirmModal;
    setActionLoadingId(id);
    try {
      if (type === 'accept') {
        await fetchAcceptLead(id);
        setToast({ message: '已成功接受该线索', type: 'success' });
      } else if (type === 'decline') {
        await fetchDeclineLead(id);
        setToast({ message: '已拒绝该线索', type: 'info' });
      } else if (type === 'close') {
        await fetchCloseLead(id);
        setToast({ message: '已成功关闭线索', type: 'success' });
      }
      setConfirmModal(null);
      await loadLeads();
    } catch (err: any) {
      setToast({
        message:
          err instanceof Error
            ? err.message
            : type === 'accept'
            ? '接受线索失败'
            : type === 'decline'
            ? '拒绝线索失败'
            : '关闭线索失败',
        type: 'error',
      });
    } finally {
      setActionLoadingId(null);
    }
  };



  return (
    <AccountLayout
      activePath="/my-leads"
      breadcrumbs={[{ label: '账号中心', href: '/profile' }, { label: '合作线索' }]}
    >
      {/* 头部标题与描述 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <div>
          <h1 className="font-bold text-xl text-slate-900">我的合作线索</h1>
          <p className="text-xs text-slate-500 mt-1">管理与您发布或发送的合作与招聘线索</p>
        </div>
      </div>

          {/* View Toggle */}
          {isEnterprise && (
            <div className="flex items-center gap-2 mb-6">
              <button
                onClick={() => handleTabChange('received')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${viewType === 'received'
                    ? 'bg-primary-500 text-foreground-950'
                    : 'text-foreground-600 hover:bg-gray-50'
                  }`}
              >
                收到的线索
              </button>
              <button
                onClick={() => handleTabChange('sent')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${viewType === 'sent'
                    ? 'bg-primary-500 text-foreground-950'
                    : 'text-foreground-600 hover:bg-gray-50'
                  }`}
              >
                发出的线索
              </button>
            </div>
          )}



          {/* Content Area */}
          {loading ? (
            <div className="text-center py-20">
              <i className="ri-loader-4-line text-3xl text-primary-500 animate-spin mb-3"></i>
              <p className="text-sm text-foreground-500">正在加载合作线索...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
                <i className="ri-error-warning-line text-2xl text-red-500"></i>
              </div>
              <p className="text-sm text-red-600 mb-3">{error}</p>
              <button
                onClick={loadLeads}
                className="px-4 py-2 rounded-lg bg-primary-500 text-white text-xs font-medium hover:bg-primary-600 transition-all cursor-pointer"
              >
                重新加载
              </button>
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <i className={`${viewType === 'received' ? 'ri-inbox-line' : 'ri-send-plane-line'} text-2xl text-foreground-400`}></i>
              </div>
              <p className="text-sm text-foreground-500 mb-3">
                {viewType === 'received' ? '暂无收到的合作线索' : '暂无发出的合作线索'}
              </p>
              {viewType === 'sent' && (
                <Link
                  to="/talents"
                  className="text-sm text-primary-600 hover:text-primary-500 cursor-pointer font-medium"
                >
                  前往人才大厅寻找合作主体
                </Link>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {leads.map((lead) => {
                const badge = getStatusBadge(lead.status);
                const statusUpper = (lead.status || '').toUpperCase();
                const canRespondLead = statusUpper === 'SENT' || statusUpper === 'PENDING' || statusUpper === 'NEW';
                const canCloseLead = statusUpper === 'SENT' || statusUpper === 'PENDING' || statusUpper === 'NEW';
                const isOperating = actionLoadingId === lead.id;

                return (
                  <div
                    key={lead.id}
                    className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-200/80 hover:border-primary-200 hover:shadow-sm transition-all duration-200"
                  >
                    {/* Header: Badges & Creation Time */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 pb-4 border-b border-slate-100">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium border ${badge.color}`}>
                          {badge.label}
                        </span>
                        {lead.lead_type && (
                          <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-primary-50 text-primary-700 border border-primary-100">
                            {lead.lead_type === 'COOPERATION' ? '合作意向' : lead.lead_type === 'RECRUITMENT' ? '招聘需求' : lead.lead_type}
                          </span>
                        )}
                      </div>
                      {lead.created_at && (
                        <div className="flex items-center gap-1.5 text-xs text-slate-400">
                          <i className="ri-time-line text-sm"></i>
                          <span>创建于 {new Date(lead.created_at).toLocaleString()}</span>
                        </div>
                      )}
                    </div>

                    {/* Body: Contact Info & Intention */}
                    <div className="py-4 space-y-3.5">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
                            <i className="ri-user-3-line text-sm"></i>
                          </div>
                          <span className="text-sm font-bold text-slate-900">{lead.contact_name || '未提供名称'}</span>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          {lead.contact_phone && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-100 text-xs font-medium">
                              <i className="ri-phone-line text-sm"></i>
                              {lead.contact_phone}
                            </span>
                          )}
                          {lead.contact_email && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-sky-50 text-sky-700 border border-sky-100 text-xs font-medium">
                              <i className="ri-mail-line text-sm"></i>
                              {lead.contact_email}
                            </span>
                          )}
                          {lead.contact_wechat && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-100 text-xs font-medium">
                              <i className="ri-wechat-line text-sm"></i>
                              {lead.contact_wechat}
                            </span>
                          )}
                        </div>
                      </div>

                      {lead.intention && (
                        <div className="p-4 rounded-xl bg-slate-50/80 border border-slate-100">
                          <p className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5">
                            <i className="ri-chat-1-line text-primary-500"></i>
                            合作意向说明：
                          </p>
                          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                            {lead.intention}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Timeline & Actions */}
                    {(lead.accepted_at || lead.declined_at || lead.closed_at || (viewType === 'received' && canRespondLead) || (viewType === 'sent' && canCloseLead)) && (
                      <div className="pt-3 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400">
                          {lead.accepted_at && (
                            <span className="flex items-center gap-1 text-emerald-600 font-medium">
                              <i className="ri-checkbox-circle-line text-sm"></i>
                              已于 {new Date(lead.accepted_at).toLocaleString()} 接受
                            </span>
                          )}
                          {lead.declined_at && (
                            <span className="flex items-center gap-1 text-rose-600 font-medium">
                              <i className="ri-close-circle-line text-sm"></i>
                              已于 {new Date(lead.declined_at).toLocaleString()} 拒绝
                            </span>
                          )}
                          {lead.closed_at && (
                            <span className="flex items-center gap-1 text-slate-500">
                              <i className="ri-shut-down-line text-sm"></i>
                              已于 {new Date(lead.closed_at).toLocaleString()} 关闭
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2.5 justify-end">
                          {viewType === 'received' && canRespondLead && (
                            <>
                              <button
                                onClick={() => handleOpenConfirmModal('accept', lead.id)}
                                disabled={isOperating}
                                className="px-4 py-2 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-1.5 shadow-2xs"
                              >
                                {isOperating ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line text-sm"></i>}
                                接受线索
                              </button>
                              <button
                                onClick={() => handleOpenConfirmModal('decline', lead.id)}
                                disabled={isOperating}
                                className="px-4 py-2 rounded-xl border border-rose-200 text-rose-600 hover:bg-rose-50 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-1.5"
                              >
                                {isOperating ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-close-line text-sm"></i>}
                                拒绝线索
                              </button>
                            </>
                          )}

                          {viewType === 'sent' && canCloseLead && (
                            <button
                              onClick={() => handleOpenConfirmModal('close', lead.id)}
                              disabled={isOperating}
                              className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-1.5"
                            >
                              {isOperating ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-close-circle-line text-sm"></i>}
                              关闭线索
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

      {/* 确认操作弹窗 */}
      <ConfirmModal
        open={Boolean(confirmModal)}
        type={confirmModal?.type === 'decline' ? 'danger' : 'warning'}
        title={
          confirmModal?.type === 'accept'
            ? '接受合作线索'
            : confirmModal?.type === 'decline'
            ? '拒绝合作线索'
            : '关闭合作线索'
        }
        content={
          confirmModal?.type === 'accept'
            ? '确认要接受该合作线索吗？接受后将建立合作沟通。'
            : confirmModal?.type === 'decline'
            ? '确认要拒绝该合作线索吗？拒绝后该线索将被标记为已拒绝。'
            : '确认要关闭该合作线索吗？关闭后将无法继续对该线索发起后续操作。'
        }
        confirmText={
          confirmModal?.type === 'accept'
            ? '确认接受'
            : confirmModal?.type === 'decline'
            ? '确认拒绝'
            : '确认关闭'
        }
        cancelText="取消"
        loading={Boolean(actionLoadingId)}
        onConfirm={handleExecuteAction}
        onCancel={() => setConfirmModal(null)}
      />

      {/* 消息提示 */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </AccountLayout>
  );
}
